import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { INotesInputComponent } from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue, ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IInputOptions } from '@modeso/types__aumico-lib-notes-be';
import { IInputDataType } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/input-type.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';

import { IAnnualReportDefaultSettings } from '../../interfaces/IAnnualReportDefaultSettings.interface';

import { NotesPageBase } from '../NotesPageBase';
import { IInputDelegate } from './IInputDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:InputComponent');

@UntilDestroy()
@Component({
    selector: 'aumico-notes-input',
    templateUrl: 'input.component.html',
    styleUrls: ['input.component.scss'],
})
export class InputComponent extends NotesPageBase implements OnInit {
    @Input() language: string = 'de-ch';
    @Input() languages: string[];
    @Input() isExtraSettingsOn: boolean;

    private _componentOptions: IInputOptions;
    title: string;
    @Input() set componentData(value: INotesInputComponent) {
        this.fillFormWithSavedData(value);
    }

    @Input() set annualReportDefaultSettings(value: IAnnualReportDefaultSettings) {
        this.currentYearDate = value.currentBalanceDate;
        this.previousYearDate = value.previousBalanceDate;
        this.currency = value.currency;
    }

    @Input() set componentOptions(value: IInputOptions | undefined) {
        if (value == null) {
            return;
        }
        this._componentOptions = value;
        this.text = value.text;
        this.dataType = value.dataType;
        this.numberOfDecimalPlaces = value.numberOfDecimalPlaces;
        this.compareYears = value.compareYears;
        this.title = value.title?.[this.language];
    }

    get componentOptions(): IInputOptions {
        return this._componentOptions;
    }

    @Input() componentIndex: number;

    currentYearDate = new Date().toDateString();
    previousYearDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toDateString();
    currency = 'CHF';

    @Input() delegate: IInputDelegate;
    @Input() previewMode = false;
    @Input() viewMode: ViewMode = ViewMode.EDIT;

    form: UntypedFormGroup;

    text: IDynamicKeyValue | undefined;
    dataType: IInputDataType = IInputDataType.TEXT;
    numberOfDecimalPlaces = 2;
    compareYears = true;
    inputData: INotesInputComponent;

    @Input() highlightTodos = false;

    @Output() onStatusChange = new EventEmitter<boolean>();

    constructor() {
        super();
        this.initializeForm();
    }

    ngOnInit(): void {
        debug('ngOnInit');
    }

    private initializeForm(): void {
        this.form = new FormGroup({
            currentYearValue: new UntypedFormControl(),
            previousYearValue: new UntypedFormControl(),
        });

        this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            this.inputData = {
                options: this.componentOptions,
                currentYearValue: value.currentYearValue,
                previousYearValue: value.previousYearValue,
            };

            if (this.delegate != null) {
                this.delegate.inputComponentData(this.inputData, this.componentIndex);
            }
        });
    }

    onFormChange(inputOptionsMappedValue: IInputOptions): void {
        this.inputData = {
            ...this.inputData,
            options: inputOptionsMappedValue,
        };

        this.title = this.inputData.options?.title?.[this.language];

        if (this.delegate != null) {
            this.delegate.inputComponentData(this.inputData, this.componentIndex);
        }
    }
    onFormStatusChange(isValid: boolean): void {
        this.onStatusChange.emit(isValid);
    }

    private fillFormWithSavedData(inputData: INotesInputComponent): void {
        this.form.get('currentYearValue').setValue(inputData.currentYearValue);
        this.form.get('previousYearValue').setValue(inputData.previousYearValue);
        this.text = inputData.options.text;
        this.dataType = inputData.options.dataType;
        this.numberOfDecimalPlaces = inputData.options.numberOfDecimalPlaces;
        this.compareYears = inputData.options.compareYears;
    }
}
